import { z } from 'zod';
import { dayMarkKind, MOOD_WORDS } from './mood.js';
/**
 * The door: say anything, and the app files it — with receipts.
 *
 * Release 4 of 2.0 (docs/PRD-2.0.md §3). What a model may propose from what
 * the person said, and the rules that keep it a sorter rather than an author:
 *
 *   - EVERY PICK QUOTES THE WORDS IT CAME FROM, and the server checks the
 *     quote is verbatim in the text before the pick is shown. A pick with no
 *     quote, or an invented one, is dropped, not shown.
 *   - A WORD BECOMES A BAND, NEVER A FIGURE. The model may name one of the
 *     seven mood words; the figure is BAND_FIGURE until he sets one himself.
 *   - NOTHING IS WRITTEN UNTIL HE FILES IT.
 */
export const moodWord = z.enum(MOOD_WORDS.map((w) => w.word));
/**
 * The figure a band files as, until he sets one. Not the midpoint rounded
 * — the midpoint of Alright (1 to 2) is 1.5 and the scale is integers — but
 * the band's lower-magnitude end, so a word never files as more than it said:
 * "pretty alright" is +1, not +2; "rough" is −2, not −3. Great and Awful are
 * one point wide at the ends and file as the only figures they can.
 */
export const BAND_FIGURE = {
    Awful: -4,
    Rough: -2,
    'A bit low': -1,
    Neutral: 0,
    Alright: 1,
    Good: 3,
    Great: 5,
};
/** What the model returns. Every quote is checked against the text on the server. */
export const doorProposalSchema = z.object({
    mood: z
        .object({
        band: moodWord.describe('One of the seven words for how they say they are NOW, overall. Never a number.'),
        quote: z.string().describe('The exact words, copied verbatim, that say how they are.'),
    })
        .nullable()
        .describe('Null unless they said how they are feeling overall right now.'),
    doses: z
        .array(z.object({
        medication: z.string().describe('The medication name as they said it.'),
        status: z.enum(['taken', 'skipped', 'missed']),
        time: z
            .string()
            .regex(/^([01]\d|2[0-3]):[0-5]\d$/)
            .nullable()
            .describe('HH:MM, 24-hour, if they said roughly when they took it; else null. "around nine" in the morning is 09:00.'),
        quote: z.string(),
    }))
        .describe('Only medications they actually mentioned taking, skipping or missing.'),
    marks: z
        .array(z.object({ kind: dayMarkKind, quote: z.string() }))
        .describe('Day marks the words support: saw_people (met or spent time with someone), short_night (slept badly or little), exercise (a run, a workout, a walk for exercise), sex, alcohol (a drink), unwell (sick, in pain), no_plans, argument (a fight, a row). Only what they SAID.'),
    people: z
        .array(z.object({ name: z.string(), quote: z.string() }))
        .describe('People mentioned, by the name used, exactly as written. Only people. Never invent one.'),
    worksheet: z
        .object({
        format: z.enum(['standard', 'rejection', 'stuck', 'conflict', 'catastrophe']),
        quote: z.string(),
    })
        .nullable()
        .describe('Non-null ONLY if they describe a conflict, a rejection they are spiralling on, feeling stuck starting something, or catastrophising — something a worksheet is for. Otherwise null. This becomes a question, never a filing.'),
});
/** A pick as the client sees it: grounded, labelled, with its receipt. */
export const doorPickKind = z.enum(['mood', 'dose', 'mark', 'person']);
/** One pick as filing sends it back: exactly as proposed. */
export const doorFilePick = z.object({
    id: z.string(),
    payload: z.discriminatedUnion('kind', [
        z.object({ kind: z.literal('mood'), band: moodWord, mood: z.number().int().min(-5).max(5) }),
        z.object({
            kind: z.literal('dose'),
            medicationId: z.string(),
            scheduledFor: z.string().nullable(),
            status: z.enum(['taken', 'skipped', 'missed']),
            takenAt: z.string().nullable(),
        }),
        z.object({ kind: z.literal('mark'), markKind: dayMarkKind }),
        z.object({ kind: z.literal('person'), name: z.string().min(1).max(40) }),
    ]),
});
/** What filing sends back: the picks he kept. */
export const doorFileBody = z.object({
    voiceLogId: z.string(),
    picks: z.array(doorFilePick),
});
/**
 * Taking one back out, after "Ask before filing" filed it (14 Oct 2026): the
 * same pick, and the row it wrote goes. A mark that was his already stays.
 */
export const doorUnfileBody = z.object({ voiceLogId: z.string(), pick: doorFilePick });
export const doorCorrectBody = z.object({ kind: doorPickKind, key: z.string().min(1).max(80) });
//# sourceMappingURL=door.js.map